import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Building2, Plus, Edit2, Trash2, Users, FileText, Shield, Settings,
  LayoutDashboard, Upload, List, ClipboardCheck, FileBarChart, CreditCard, LogOut, Menu, X
} from "lucide-react";

const CATEGORIES = [
  "Income", "Direct Expenses", "Indirect Expenses", "Internal Transfer", "VAT Payments",
  "Loan Repayments", "Owner Drawings", "Wages", "Marketing", "Software Subscriptions",
  "Rent", "Utilities", "Insurance", "Professional Fees", "Travel", "Other"
];

const EXPENSE_TYPES = ["direct", "indirect", "transfer", "other"];

export default function AdminPage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({ pattern: "", category: "Other", expense_type: "other", priority: 100 });
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error("Access denied. Admin role required.");
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rulesRes, usersRes, logsRes] = await Promise.all([
        axios.get(`${API}/classification-rules`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/users`, { headers, withCredentials: true }),
        axios.get(`${API}/audit-logs?limit=50`, { headers, withCredentials: true })
      ]);
      setRules(rulesRes.data);
      setUsers(usersRes.data);
      setAuditLogs(logsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRule = async () => {
    if (!ruleForm.pattern.trim()) {
      toast.error("Pattern is required");
      return;
    }
    setSaving(true);
    try {
      if (editingRule) {
        await axios.put(`${API}/classification-rules/${editingRule.rule_id}`, ruleForm, { headers, withCredentials: true });
        toast.success("Rule updated");
      } else {
        await axios.post(`${API}/classification-rules`, ruleForm, { headers, withCredentials: true });
        toast.success("Rule created");
      }
      setShowRuleDialog(false);
      setEditingRule(null);
      setRuleForm({ pattern: "", category: "Other", expense_type: "other", priority: 100 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm("Delete this classification rule?")) return;
    try {
      await axios.delete(`${API}/classification-rules/${ruleId}`, { headers, withCredentials: true });
      toast.success("Rule deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete rule");
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const formData = new FormData();
      formData.append("role", newRole);
      await axios.put(`${API}/admin/users/${userId}/role`, formData, { headers, withCredentials: true });
      toast.success("Role updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const startEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      pattern: rule.pattern,
      category: rule.category,
      expense_type: rule.expense_type || "other",
      priority: rule.priority || 100
    });
    setShowRuleDialog(true);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('en-GB');
    } catch {
      return dateStr;
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLink = ({ to, icon: Icon, children, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${active ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'}`}>
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{children}</span>
    </Link>
  );

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#121214] border-r border-zinc-800 hidden lg:flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1C1917]" />
            </div>
            <div>
              <h1 className="font-semibold text-white">BankFlow Control</h1>
              <p className="text-xs text-zinc-500">Financial Analysis</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/upload" icon={Upload}>Upload Statements</NavLink>
          <NavLink to="/transactions" icon={List}>Transactions</NavLink>
          <NavLink to="/review" icon={ClipboardCheck}>Review Queue</NavLink>
          <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
          <NavLink to="/accounts" icon={CreditCard}>Bank Accounts</NavLink>
          <NavLink to="/admin" icon={Shield} active>Admin Settings</NavLink>
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-white">{user?.name?.charAt(0) || 'U'}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121214] border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1C1917]" />
            </div>
            <span className="font-semibold text-white">BankFlow</span>
          </Link>
          <Button variant="ghost" size="icon" className="text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-white">Admin Settings</h1>
            <p className="text-zinc-400 text-sm mt-1">Manage classification rules, users, and view audit logs</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#FCD34D] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Tabs defaultValue="rules" className="space-y-6">
              <TabsList className="bg-zinc-900/50 border border-zinc-800">
                <TabsTrigger value="rules" className="data-[state=active]:bg-[#FCD34D] data-[state=active]:text-[#1C1917]">
                  <Settings className="w-4 h-4 mr-2" />
                  Classification Rules
                </TabsTrigger>
                <TabsTrigger value="users" className="data-[state=active]:bg-[#FCD34D] data-[state=active]:text-[#1C1917]">
                  <Users className="w-4 h-4 mr-2" />
                  Users
                </TabsTrigger>
                <TabsTrigger value="audit" className="data-[state=active]:bg-[#FCD34D] data-[state=active]:text-[#1C1917]">
                  <FileText className="w-4 h-4 mr-2" />
                  Audit Log
                </TabsTrigger>
              </TabsList>

              {/* Classification Rules Tab */}
              <TabsContent value="rules">
                <Card className="bg-[#121214] border-zinc-800">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg text-white">Classification Rules</CardTitle>
                      <CardDescription className="text-zinc-500">
                        Define patterns to auto-categorize transactions
                      </CardDescription>
                    </div>
                    <Dialog open={showRuleDialog} onOpenChange={(open) => { setShowRuleDialog(open); if (!open) { setEditingRule(null); setRuleForm({ pattern: "", category: "Other", expense_type: "other", priority: 100 }); } }}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]" data-testid="add-rule-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Rule
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#121214] border-zinc-800">
                        <DialogHeader>
                          <DialogTitle className="text-white">{editingRule ? 'Edit Rule' : 'Add Classification Rule'}</DialogTitle>
                          <DialogDescription className="text-zinc-400">
                            Define a pattern to match transaction descriptions
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Pattern (regex)</Label>
                            <Input
                              placeholder="e.g., salary|wage|payroll"
                              value={ruleForm.pattern}
                              onChange={(e) => setRuleForm({ ...ruleForm, pattern: e.target.value })}
                              className="bg-zinc-900/50 border-zinc-800 text-white"
                            />
                            <p className="text-xs text-zinc-500">Use | for OR, case-insensitive matching</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Category</Label>
                            <Select value={ruleForm.category} onValueChange={(v) => setRuleForm({ ...ruleForm, category: v })}>
                              <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#121214] border-zinc-800">
                                {CATEGORIES.map(cat => (
                                  <SelectItem key={cat} value={cat} className="text-white hover:bg-zinc-800">{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Expense Type</Label>
                            <Select value={ruleForm.expense_type} onValueChange={(v) => setRuleForm({ ...ruleForm, expense_type: v })}>
                              <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#121214] border-zinc-800">
                                {EXPENSE_TYPES.map(type => (
                                  <SelectItem key={type} value={type} className="text-white hover:bg-zinc-800 capitalize">{type}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Priority (higher = checked first)</Label>
                            <Input
                              type="number"
                              value={ruleForm.priority}
                              onChange={(e) => setRuleForm({ ...ruleForm, priority: parseInt(e.target.value) || 100 })}
                              className="bg-zinc-900/50 border-zinc-800 text-white"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowRuleDialog(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                            Cancel
                          </Button>
                          <Button onClick={handleSaveRule} disabled={saving} className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]">
                            {saving ? <div className="w-4 h-4 border-2 border-[#1C1917] border-t-transparent rounded-full animate-spin" /> : editingRule ? "Update" : "Create"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800 hover:bg-transparent">
                          <TableHead className="text-zinc-400">Pattern</TableHead>
                          <TableHead className="text-zinc-400">Category</TableHead>
                          <TableHead className="text-zinc-400">Type</TableHead>
                          <TableHead className="text-zinc-400">Priority</TableHead>
                          <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rules.map((rule) => (
                          <TableRow key={rule.rule_id} className="border-zinc-800">
                            <TableCell className="font-mono text-sm text-white">{rule.pattern}</TableCell>
                            <TableCell>
                              <Badge className="badge-neutral">{rule.category}</Badge>
                            </TableCell>
                            <TableCell className="text-zinc-400 capitalize">{rule.expense_type || 'other'}</TableCell>
                            <TableCell className="text-zinc-400">{rule.priority}</TableCell>
                            <TableCell className="text-right">
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-white hover:bg-zinc-800" onClick={() => startEditRule(rule)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-red-500 hover:bg-zinc-800" onClick={() => handleDeleteRule(rule.rule_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users">
                <Card className="bg-[#121214] border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">User Management</CardTitle>
                    <CardDescription className="text-zinc-500">
                      Manage user roles and permissions
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800 hover:bg-transparent">
                          <TableHead className="text-zinc-400">User</TableHead>
                          <TableHead className="text-zinc-400">Email</TableHead>
                          <TableHead className="text-zinc-400">Role</TableHead>
                          <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((u) => (
                          <TableRow key={u.user_id} className="border-zinc-800">
                            <TableCell className="text-white">{u.name}</TableCell>
                            <TableCell className="text-zinc-400">{u.email}</TableCell>
                            <TableCell>
                              <Badge className={
                                u.role === 'admin' ? 'bg-[#FCD34D]/10 text-[#FCD34D] border-[#FCD34D]/20' :
                                u.role === 'finance' ? 'badge-success' : 'badge-neutral'
                              }>
                                {u.role || 'finance'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Select value={u.role || 'finance'} onValueChange={(v) => handleUpdateRole(u.user_id, v)}>
                                <SelectTrigger className="w-32 bg-zinc-900/50 border-zinc-800 text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#121214] border-zinc-800">
                                  <SelectItem value="admin" className="text-white hover:bg-zinc-800">Admin</SelectItem>
                                  <SelectItem value="finance" className="text-white hover:bg-zinc-800">Finance</SelectItem>
                                  <SelectItem value="read_only" className="text-white hover:bg-zinc-800">Read Only</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Audit Log Tab */}
              <TabsContent value="audit">
                <Card className="bg-[#121214] border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Audit Log</CardTitle>
                    <CardDescription className="text-zinc-500">
                      Immutable record of all system changes
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800 hover:bg-transparent">
                          <TableHead className="text-zinc-400">Timestamp</TableHead>
                          <TableHead className="text-zinc-400">User</TableHead>
                          <TableHead className="text-zinc-400">Action</TableHead>
                          <TableHead className="text-zinc-400">Entity</TableHead>
                          <TableHead className="text-zinc-400">Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {auditLogs.map((log) => (
                          <TableRow key={log.log_id} className="border-zinc-800">
                            <TableCell className="text-zinc-400 font-mono text-xs">{formatDate(log.timestamp)}</TableCell>
                            <TableCell className="text-white">{log.user_email}</TableCell>
                            <TableCell>
                              <Badge className={
                                log.action === 'create' ? 'badge-success' :
                                log.action === 'update' ? 'badge-warning' :
                                log.action === 'delete' ? 'badge-error' : 'badge-neutral'
                              }>
                                {log.action}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-zinc-400">{log.entity_type}</TableCell>
                            <TableCell className="text-zinc-500 text-xs max-w-xs truncate">
                              {JSON.stringify(log.changes).slice(0, 100)}...
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
    </div>
  );
}
